import { useCallback } from "react";
import { useHistory, useLocation } from "react-router-dom";

import { TeacherClassesSendParentInvitesResponseBody } from "@seneca/class-service-types";
import {
  Box,
  Button,
  Heading,
  Icon,
  Inline,
  Modal,
  Stack,
  Text,
  Tooltip,
  useBreakpoint,
  useTheme
} from "@seneca/senekit";

import { getClassName } from "seneca-common/features/class/state";
import { getUserExternalSchoolId } from "seneca-common/features/user/state";
import {
  useSelector,
  useSelectorOnConditionWithArgs
} from "seneca-common/utils/hooks/selectors";
import useEffectOnMount from "seneca-common/utils/hooks/useEffectOnMount";

import { ModalPortal } from "features/common/modals/components/ModalNodeContext";
import {
  analyticsLogInviteAllParentsTile,
  analyticsLogSendParentInvite
} from "features/teachers/features/analytics/class-management";
import {
  inviteAllParentsTileActions,
  SendTeacherParentInviteSources
} from "features/teachers/features/analytics/class-management/consts";
import useBatchSendTeacherClassesParentInvites from "features/teachers/hooks/useBatchSendTeacherClassesParentInvites";
import useGetTeacherDisplayName from "features/teachers/hooks/useGetTeacherDisplayName";
import useCanInviteAllParents from "features/teachers/pages/homepage/utils/useCanInviteAllParents";
import { PreselectClassesOption } from "features/teachers/routes/teacher/routes/classes/links";

import EmailInvitationTemplate from "./EmailInvitationTemplate";
import InviteClassesParentsModalSuccess from "./InviteClassesParentsModalSuccess";

export const INVITE_PARENTS_ROUTE = "invite-parents";

export default function InviteClassesParentsModal({
  classIds,
  ineligibleClassIds
}: {
  classIds: string[];
  ineligibleClassIds?: string[];
}) {
  const theme = useTheme();
  const history = useHistory();

  const teacherDisplayName = useGetTeacherDisplayName();

  const singleClassSelectionId = classIds?.length ? classIds[0] : null;

  const className = useSelectorOnConditionWithArgs(
    singleClassSelectionId,
    getClassName,
    singleClassSelectionId
  );

  const analyticsInviteSource = useGetAnalyticsInviteSource({ classIds });

  const { updateLastInviteSent } = useCanInviteAllParents();

  const externalSchoolId = useSelector(getUserExternalSchoolId);

  const onSuccess = useCallback(
    (data: TeacherClassesSendParentInvitesResponseBody | undefined) => {
      if (!classIds) updateLastInviteSent();

      analyticsLogSendParentInvite({
        inviteSource: analyticsInviteSource,
        numParentsInvited: data ? data.parentInvitesCount : 0,
        externalSchoolId
      });
    },
    [updateLastInviteSent, classIds, analyticsInviteSource, externalSchoolId]
  );

  const {
    mutate: sendBatchParentInvite,
    isLoading,
    isSuccess
  } = useBatchSendTeacherClassesParentInvites({ onSuccess });

  useEffectOnMount(() => {
    if (!classIds) {
      analyticsLogInviteAllParentsTile({
        actionName: inviteAllParentsTileActions.inviteParents
      });
    }
  });

  const onCompleteHandler = () => {
    const { pathname } = history.location;
    const state: {
      deselectClasses: PreselectClassesOption;
    } = {
      deselectClasses: "all"
    };
    history.replace(pathname.replace(INVITE_PARENTS_ROUTE, ""), state);
  };

  return (
    <ModalPortal>
      <Modal fullscreenOnMobile onCloseClick={history.goBack}>
        {isSuccess ? (
          <Box margin="auto" width="300px" height="300px">
            <InviteClassesParentsModalSuccess onComplete={onCompleteHandler} />
          </Box>
        ) : (
          <Stack space="xlarge">
            <Stack space="12">
              <Heading level="4">Invite parents at your school</Heading>
              <Inline space="large" alignItems="center">
                {classIds && classIds.length === 1 && (
                  <Inline>
                    <Text>Inviting parents from class &nbsp;</Text>
                    <Text weight="bold">{className}</Text>
                  </Inline>
                )}
                {classIds && classIds.length > 1 && (
                  <Text>
                    Inviting parents from {classIds.length} managed classes
                  </Text>
                )}
                {!!ineligibleClassIds?.length && (
                  <IneligibleInviteClassesTooltip
                    ineligibleClassIds={ineligibleClassIds}
                  />
                )}
              </Inline>
              <Text>
                Invited parents will get an email with an invite link, they
                simply need to click on the link and sign up to Seneca for free
                to monitor their child's progress.
              </Text>
            </Stack>
            <Box
              backgroundColor={theme.foreground}
              padding="12"
              borderRadius="medium"
              borderStyle="dashed"
              borderWidth="xxxsmall"
              borderColor={theme["neutral-low-content"]}
            >
              <EmailInvitationTemplate teacherName={teacherDisplayName} />
            </Box>
            <Box display="flex" justifyContent="flex-end">
              <Button
                onClick={() => {
                  sendBatchParentInvite(classIds);
                }}
                loading={isLoading}
                disabled={isSuccess}
              >
                Send invites
              </Button>
            </Box>
          </Stack>
        )}
      </Modal>
    </ModalPortal>
  );
}

function useGetAnalyticsInviteSource({ classIds }: { classIds?: string[] }) {
  const { state } = useLocation<{ analyticsSource?: string }>();

  const {
    nonSltHomepageParents,
    classesBatchInvite,
    sltInviteAllParentsModal
  } = SendTeacherParentInviteSources;

  if (state?.analyticsSource === nonSltHomepageParents) {
    return nonSltHomepageParents;
  }

  if (classIds) return classesBatchInvite;

  return sltInviteAllParentsModal;
}

function IneligibleInviteClassesTooltip({
  ineligibleClassIds
}: {
  ineligibleClassIds: string[];
}) {
  const { isMobileOnly } = useBreakpoint();

  return (
    <Tooltip
      tooltip={`We're unable to send parent invites for ${ineligibleClassIds.length} of the selected classes. Parent invites can only be sent for synced classes with valid parents.`}
      contrast="high"
      position={isMobileOnly ? "bottomLeft" : "bottom"}
      maxWidth={isMobileOnly ? "85vw" : "24rem"}
      showArrow
    >
      <Box>
        <Icon
          tone="warning"
          iconName="help"
          size="small"
          accessibilityLabel="Unable to send invites for some classes help icon"
        />
      </Box>
    </Tooltip>
  );
}
